import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ClockIcon } from '@heroicons/react/24/outline';

const TimeSlotPicker = ({ value, onChange }) => {
    const [showPicker, setShowPicker] = useState(false);
    const pickerRef = useRef(null);
    const listRef = useRef(null);

    // Generate time slots (30 min intervals)
    const timeSlots = [];
    for (let i = 0; i < 24; i++) {
        for (let j = 0; j < 60; j += 30) {
            const hour = i.toString().padStart(2, '0');
            const minute = j.toString().padStart(2, '0');
            timeSlots.push(`${hour}:${minute}`);
        }
    }

    // Close picker when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (pickerRef.current && !pickerRef.current.contains(event.target)) {
                setShowPicker(false);
            }
        };

        if (showPicker) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showPicker]);

    // Scroll to selected time when picker opens
    useEffect(() => {
        if (showPicker && listRef.current && value) {
            const selectedElement = document.getElementById(`time-slot-${value}`);
            if (selectedElement) {
                selectedElement.scrollIntoView({ block: 'center' });
            }
        }
    }, [showPicker, value]);

    const formatTimeDisplay = (timeStr) => {
        if (!timeStr) return 'Select time';
        const [hour, minute] = timeStr.split(':');
        const h = parseInt(hour, 10);
        const ampm = h >= 12 ? 'PM' : 'AM';
        const hour12 = h % 12 || 12;
        return `${hour12}:${minute} ${ampm}`;
    };

    return (
        <div className='relative' ref={pickerRef}>
            {/* Input Display */}
            <button
                type='button'
                onClick={() => setShowPicker(!showPicker)}
                className='w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-900 font-medium text-left focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500 transition-all flex items-center justify-between'
            >
                <div className='flex items-center gap-2'>
                    <ClockIcon className='w-5 h-5 text-slate-400' />
                    <span className={value ? 'text-slate-900' : 'text-slate-400'}>
                        {formatTimeDisplay(value)}
                    </span>
                </div>
                <svg className='w-5 h-5 text-slate-400' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
                </svg>
            </button>

            {/* Picker Dropdown */}
            <AnimatePresence>
                {showPicker && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.15 }}
                        className='absolute top-full left-0 mt-2 bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden z-50 w-full max-h-60 overflow-y-auto'
                        ref={listRef}
                    >
                        {timeSlots.map((time) => (
                            <button
                                key={time}
                                id={`time-slot-${time}`}
                                type='button'
                                onClick={() => {
                                    onChange(time);
                                    setShowPicker(false);
                                }}
                                className={`w-full px-4 py-2.5 text-left text-sm font-medium transition-colors hover:bg-slate-50 flex items-center justify-between
                                    ${value === time ? 'bg-blue-50 text-blue-600' : 'text-slate-700'}
                                `}
                            >
                                <span>{formatTimeDisplay(time)}</span>
                                {value === time && (
                                    <svg className='w-4 h-4 text-blue-600' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                                        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 13l4 4L19 7' />
                                    </svg>
                                )}
                            </button>
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default TimeSlotPicker;
